import { createApi } from '@reduxjs/toolkit/query/react';
import { FMH_NPI, FMH_UNMANAGED_NPI, type NetworkStatus, NetworkStatuses } from '@strata/constants';
import { formatNameLastFirst, sortAsc } from '@strata/helper-functions';

import { REFERENCE_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { type PayerFeature } from 'services/FeatureFlags';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface SupplierDetails {
    npi: string;
    name: string;
    phone?: string;
    fax?: string;
    email?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    networkStatus: NetworkStatus;
}

export type SupplierMap = Record<string, Pick<SupplierDetails, 'name' | 'networkStatus'>>;

export interface PrescriberDetails {
    npi: string;
    name: string;
    phone?: string;
    fax?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    specialization?: string;
    practiceNpi?: string;
    organizationName?: string;
    pointOfContactFirstName?: string;
    pointOfContactLastName?: string;
    pointOfContactPhone?: string;
}

export interface SegmentData {
    segmentsList: { id: string; name: string }[];
    segmentsMap: Record<string, string>;
}

export const referenceApi = createApi({
    baseQuery: getBaseQuery(REFERENCE_API_URL),
    reducerPath: 'referenceApi',
    endpoints: (build) => ({
        getPayerNameMap: build.query<Record<string, string>, void>({
            query: () => 'payers/',
            transformResponse: (response: { payer_id: string; payer_name: string }[]) =>
                Object.fromEntries(response.map((payer) => [payer.payer_id, payer.payer_name])),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving payer names.',
            ),
        }),
        getPayerDetails: build.query<
            {
                payerId: string;
                payerName: string;
                payerEmail?: string;
                payerFeatures?: PayerFeature[];
            },
            { payerId: string }
        >({
            query: ({ payerId }) => `payers/${payerId}`,
            transformResponse: (response: {
                payer_id: string;
                payer_name: string;
                payer_email?: string;
                features?: PayerFeature[];
            }) => ({
                payerId: response.payer_id,
                payerName: response.payer_name,
                payerEmail: response.payer_email,
                payerFeatures: response.features,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving payer details.',
            ),
        }),
        getPlaceOfServiceMap: build.query<Record<string, string>, void>({
            query: () => 'product_benefit_configs/places',
            transformResponse: (response: { code: string; description: string }[]) =>
                Object.fromEntries(
                    (response ?? []).map((option) => [option.code, option.description]),
                ),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving place of service options.',
            ),
        }),
        getPrescriberDetails: build.query<PrescriberDetails, { npi: string }>({
            query: ({ npi }) => `prescribers/${npi}`,
            transformResponse: (response: {
                prescriber_npi: string;
                first_name: string;
                last_name: string;
                organization_name?: string;
                practice_npi?: string;
                telephone_number?: string;
                fax_number?: string;
                address_1?: string;
                address_2?: string;
                city?: string;
                state?: string;
                postal_code?: string;
                specialization?: string;
                point_of_contact_first_name?: string;
                point_of_contact_last_name?: string;
                point_of_contact_phone?: string;
            }) => ({
                npi: response.prescriber_npi,
                name: formatNameLastFirst(response.last_name, response.first_name),
                phone: response.telephone_number,
                fax: response.fax_number,
                address1: response.address_1,
                address2: response.address_2,
                city: response.city,
                state: response.state,
                zip: response.postal_code,
                specialization: response.specialization,
                practiceNpi: response.practice_npi,
                organizationName: response.organization_name,
                pointOfContactFirstName: response.point_of_contact_first_name,
                pointOfContactLastName: response.point_of_contact_last_name,
                pointOfContactPhone: response.point_of_contact_phone,
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving prescriber details.', err);
                }
            },
        }),
        getSegments: build.query<SegmentData, { payerId: string }>({
            query: ({ payerId }) => `payer_segments/payers/${payerId}`,
            transformResponse: (response: { segment_id: string; segment_name: string }[]) => {
                const segmentsList = response
                    ?.map((segment) => ({
                        id: segment.segment_id,
                        name: segment.segment_name,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name));
                const segmentsMap = Object.fromEntries(
                    segmentsList.map(({ id, name }) => [id, name]),
                );
                return { segmentsList, segmentsMap };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving segment info.',
            ),
        }),
        getSupplierDetails: build.query<SupplierDetails, { npi: string }>({
            query: ({ npi }) => `suppliers/${npi}`,
            transformResponse: (response: {
                supplier_npi: string;
                organization_name: string;
                address_1?: string;
                address_2?: string;
                city?: string;
                state?: string;
                postal_code?: string;
                telephone_number?: string;
                fax_number?: string;
                email?: string;
                network_status: NetworkStatus;
            }) => ({
                name: response?.organization_name,
                phone: response?.telephone_number,
                fax: response?.fax_number,
                email: response?.email,
                address1: response?.address_1,
                address2: response?.address_2,
                city: response?.city,
                state: response?.state,
                zip: response?.postal_code,
                npi: response?.supplier_npi,
                networkStatus: response?.network_status,
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving pharmacy details.', err);
                }
            },
        }),
        getSupplierMap: build.query<SupplierMap, void>({
            query: () => 'suppliers/parents',
            transformResponse: (
                response: {
                    supplier_npi: string;
                    organization_name: string;
                    network_status: NetworkStatus;
                }[],
            ) => {
                const supplierMap = Object.fromEntries(
                    response.map((supplier) => [
                        supplier.supplier_npi,
                        {
                            name: supplier.organization_name,
                            networkStatus: supplier.network_status,
                        },
                    ]),
                );
                supplierMap[FMH_NPI] = {
                    name: 'Free Market Health',
                    networkStatus: NetworkStatuses.ACTIVE,
                };
                supplierMap[FMH_UNMANAGED_NPI] = {
                    name: 'Unmanaged',
                    networkStatus: NetworkStatuses.ACTIVE,
                };
                return supplierMap;
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving supplier information.',
            ),
        }),
        // Gets list of all Product/Services
        getAllProductServices: build.query<
            {
                productServiceList: { oid: string; name: string; secondaryData: string }[];
                productServiceMap: Record<string, string>;
            },
            void
        >({
            query: () => '/productservices/summaryonly',
            transformResponse: (response: { oid: string; description: string }[]) => {
                const productServiceList = (response ?? [])
                    .map((productService) => ({
                        oid: productService.oid,
                        name: productService.description,
                        secondaryData: productService.oid,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name));
                const productServiceMap = Object.fromEntries(
                    productServiceList.map(({ oid, name }) => [oid, name]),
                );
                return { productServiceList, productServiceMap };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving list of products.',
            ),
        }),
        getDiagnosesMap: build.query<Record<string, string>, void>({
            query: () => 'diagnoses/',
            transformResponse: (response: { oid: string; description: string }[]) =>
                Object.fromEntries(
                    response.map((diagnosis) => [diagnosis.oid, diagnosis.description]),
                ),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving diagnoses.'),
        }),
    }),
});

export const {
    useGetPayerNameMapQuery,
    useGetAllProductServicesQuery,
    useGetPayerDetailsQuery,
    useGetPlaceOfServiceMapQuery,
    useGetPrescriberDetailsQuery,
    useGetSegmentsQuery,
    useGetSupplierDetailsQuery,
    useGetSupplierMapQuery,
    useGetDiagnosesMapQuery,
} = referenceApi;
